import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchJson, postJson, putJson } from '../lib/api';

// --- Types ---

export type CaseStatus = 'open' | 'in_progress' | 'waiting' | 'escalated' | 'resolved' | 'closed';
export type CasePriority = 'low' | 'medium' | 'high' | 'urgent';

export interface Case {
  id: string;
  subject: string;
  description: string;
  status: CaseStatus;
  priority: CasePriority;
  category: string;
  assigneeId?: string;
  assigneeName?: string;
  contactId?: string;
  contactName?: string;
  organizationId?: string;
  organizationName?: string;
  policyNumber?: string;
  resolution?: string;
  slaDeadline?: string;
  escalatedAt?: string;
  resolvedAt?: string;
  closedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CaseFilters {
  status?: CaseStatus;
  priority?: CasePriority;
  category?: string;
  assigneeId?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface CasesResponse {
  cases: Case[];
  total: number;
  limit: number;
  offset: number;
}

// --- Helpers ---

function buildQueryString(filters?: CaseFilters): string {
  if (!filters) return '';
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.priority) params.set('priority', filters.priority);
  if (filters.category) params.set('category', filters.category);
  if (filters.assigneeId) params.set('assigneeId', filters.assigneeId);
  if (filters.search) params.set('search', filters.search);
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.offset) params.set('offset', String(filters.offset));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// --- Hooks ---

export function useCases(filters?: CaseFilters) {
  return useQuery({
    queryKey: ['cases', filters],
    queryFn: () => fetchJson<CasesResponse>(`/api/cases${buildQueryString(filters)}`),
  });
}

export function useCase(id: string | null) {
  return useQuery({
    queryKey: ['case', id],
    queryFn: () => fetchJson<Case>(`/api/cases/${id}`),
    enabled: !!id,
  });
}

export function useCreateCase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Case>) => postJson<Case>('/api/cases', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cases'] });
    },
  });
}

export function useUpdateCase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Case> }) =>
      putJson<Case>(`/api/cases/${id}`, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['case', variables.id] });
    },
  });
}

export function useCloseCase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, resolution }: { id: string; resolution: string }) =>
      putJson<Case>(`/api/cases/${id}/close`, { resolution }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['case', variables.id] });
    },
  });
}

export function useEscalateCase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      putJson<Case>(`/api/cases/${id}/escalate`, { reason }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['case', variables.id] });
    },
  });
}
